import {
  Box,
  Button,
  Flex,
  Group,
  Pagination,
  Paper,
  Stack,
  Text,
  Title
} from '@mantine/core'
import {
  IconChevronRight,
  IconCircleCheckFilled,
  IconCirclePlus
} from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import { PageWithFixedToolbar } from '@/components/layout/PageWithFixedToolbar/PageWithFixedToolbar'
import { LoadingOverlay } from '@/components/ui-shared/LoadingOverlay/LoadingOverlay'
import { useTableState } from '@/components/ui-shared/Tables/useTableState'
import { useApplicationContext } from '@/providers/ApplicationContext'
import { useModelContext } from '@/providers/ModelContext'
import { useGetMlModelList, useUpdateModel } from '@/queries/modelQueries'
import {
  ApplicationNestedPath,
  buildAppLink,
  buildCreateDeploymentLink
} from '@/router/paths'
import { showToast } from '@/theme/notifications'
import { ApplicationType } from '@/types/app'
import { MLModel } from '@/types/model'
import { getPageCount, getPageOffset } from '@/utils/pagination'
import { buildSortParams } from '@/utils/sorting'
import { InferenceModal } from '../InferenceModal/InferenceModal'
import { useModelStepNavigation } from '../ModelDetails/useModelStepNavigation'
import { LibraryModelGuide } from './LibraryModelGuide'
import { LibraryModelList, TableSortConfig } from './LibraryModelList'

const PAGE_SIZE = 20

const DEFAULT_SORT_CONFIG: TableSortConfig = {
  key: 'name',
  order: 'asc'
}

export const LibraryModelListHandler = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const { application } = useApplicationContext()
  const { model } = useModelContext()

  const { goToDatasetSelectionScreen } = useModelStepNavigation({
    appId: application?.id || '',
    modelId: model?.id || ''
  })

  const { mutateAsync: updateModel, isPending: isUpdatePending } =
    useUpdateModel(model?.id || '')

  const [inferenceModel, setInferenceModel] = useState<MLModel | null>(null)

  const [tableState, setTableState] = useTableState({
    activePage: 1,
    sortConfig: DEFAULT_SORT_CONFIG
  })

  const { activePage, sortConfig } = tableState

  const { data, isFetching, isLoading, isError, refetch } = useGetMlModelList({
    is_library: true,
    is_master_model: false,
    labeling_type: model?.labeling_type, // restrict to library models matching current labeling_type and model_type to prevent selecting stage 2 as base for stage 1
    model_type: model?.model_type,
    offset: getPageOffset(activePage, PAGE_SIZE),
    limit: PAGE_SIZE,
    ordering: buildSortParams(sortConfig).ordering
  })

  const { data: masterModelsData } = useGetMlModelList({
    is_library: true,
    is_master_model: true,
    labeling_type: model?.labeling_type,
    limit: 1
  })

  const selectedModelId = model?.base_model?.id

  const libraryModels = data?.results || []

  const masterModel = masterModelsData?.results[0]

  const totalPageCount = getPageCount(data?.count || 0, PAGE_SIZE)

  useEffect(() => {
    if (totalPageCount > 0 && activePage > totalPageCount) {
      setTableState({ activePage: totalPageCount })
    }
  }, [activePage, totalPageCount, setTableState])

  const handleModelRetrain = async (baseModelId: string) => {
    if (!model) {
      return
    }

    try {
      await updateModel({
        modelId: model.id,
        data: {
          base_model_id: baseModelId
        }
      })

      void goToDatasetSelectionScreen()
    } catch {
      showToast(
        intl.formatMessage({ id: 'models.updateModelApiError' }),
        'error'
      )
    }
  }

  const handleModelDeploy = async (baseModelId: string) => {
    if (!model || !application) {
      return
    }

    try {
      await updateModel({
        modelId: model.id,
        data: {
          base_model_id: baseModelId
        }
      })

      // For multi-stage applications, we need to navigate to the models page to select the secondary model
      if (application.application_type === ApplicationType.MultiStage) {
        void navigate(
          buildAppLink(application.id, ApplicationNestedPath.models)
        )
      } else {
        void navigate(buildCreateDeploymentLink(application.id))
      }
    } catch {
      showToast(
        intl.formatMessage({ id: 'models.updateModelApiError' }),
        'error'
      )
    }
  }

  const handleTrainFromScratch = () => {
    if (!masterModel) {
      return
    }

    void handleModelRetrain(masterModel.id)
  }

  return (
    <PageWithFixedToolbar
      toolbar={
        <Title order={3}>
          <FormattedMessage id="models.library.pageTitle" />
        </Title>
      }
    >
      {model?.base_model ? (
        <Paper
          mb={60}
          p="xl"
          bg="gray.0"
          styles={{
            root: {
              borderColor: 'var(--mantine-color-green-6)',
              borderWidth: '1px'
            }
          }}
          withBorder
        >
          <Stack align="center">
            <Group wrap="nowrap" align="center">
              <IconCircleCheckFilled
                size={32}
                color="var(--mantine-color-green-6)"
              />

              <Text fw={600} size="sm" ta="center">
                {model.base_model.is_master_model ? (
                  <FormattedMessage
                    id="models.library.list.buttons.fromScratch.selected"
                    values={{ name: model.base_model.name }}
                  />
                ) : (
                  <FormattedMessage
                    id="models.library.list.buttons.libraryModel.selected"
                    values={{ name: model.base_model.name }}
                  />
                )}
              </Text>
            </Group>

            <Button
              variant="light"
              rightSection={<IconChevronRight size={16} stroke={3} />}
              onClick={() => void goToDatasetSelectionScreen()}
            >
              <FormattedMessage id="models.library.list.goNext" />
            </Button>
          </Stack>
        </Paper>
      ) : (
        <Box mb="lg">
          <LibraryModelGuide />
        </Box>
      )}

      {!!masterModel && !model?.base_model && (
        <Stack mb={60} gap="xs">
          <Button
            variant="outline"
            size="lg"
            leftSection={<IconCirclePlus size={24} />}
            fullWidth
            onClick={handleTrainFromScratch}
          >
            <FormattedMessage
              id="models.library.list.buttons.fromScratch"
              values={{ name: masterModel.name }}
            />
          </Button>

          <Text size="xs" ta="center">
            <FormattedMessage id="models.library.list.buttons.orRetrain" />
          </Text>
        </Stack>
      )}

      {!model?.base_model?.is_master_model && (
        <Box pos="relative">
          <LoadingOverlay
            visible={isFetching || isUpdatePending}
            withSpinner={false}
          />

          <Title order={3} mb="xs">
            <FormattedMessage id="models.library.list.title" />
          </Title>

          <LibraryModelList
            libraryModels={libraryModels}
            selectedModelId={selectedModelId}
            sortConfig={sortConfig}
            isLoading={isLoading || isFetching}
            hasError={isError}
            onSort={(sortConfig) =>
              setTableState({ activePage: 1, sortConfig })
            }
            onTest={(libraryModel) => setInferenceModel(libraryModel)}
            onRetrain={(libraryModel) =>
              void handleModelRetrain(libraryModel.id)
            }
            onDeploy={(libraryModel) => void handleModelDeploy(libraryModel.id)}
            onReload={() => void refetch()}
          />

          <Flex justify="end" mt="lg">
            <Pagination
              size="sm"
              total={totalPageCount}
              value={activePage}
              onChange={(value) => setTableState({ activePage: value })}
            />
          </Flex>
        </Box>
      )}

      <InferenceModal
        applicationId={application?.id}
        model={inferenceModel || undefined}
        opened={!!inferenceModel}
        onClose={() => setInferenceModel(null)}
      />
    </PageWithFixedToolbar>
  )
}
